import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { TabsScrollDirective } from '@shared/directives/tabs-scroll.directive';
import { TipoDocumentoItemCompraEnum } from '@shared/enums/compras/tipo-documento-item-compra.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import {
  classeBadgeStatusNFeEntrada,
  iconeStatusNFeEntrada,
  rotuloStatusNFeEntrada,
} from '@shared/helpers/nota-fiscal-entrada-status.helper';
import { compararValoresOrdenacao } from '@shared/helpers/ordenacao-tabela.helper';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { OrquestradorCompraService } from '@shared/services/apis/orquestrador-compras/compra.service';
import { NotaFiscalEntradaListaService } from '@shared/services/apis/repo-dfe/nota-fiscal-entrada-lista.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { ItemCompradoFornecedorStruct } from '@shared/services/structs/orquestrador-compras/item-comprado-fornecedor.struct';
import { NotaFiscalEntradaDoEmitenteStruct } from '@shared/services/structs/repo-dfe/nota-fiscal-entrada-do-emitente.struct';

export type UltimasComprasDialogData = {
  idPessoa: number;
  nomePessoa: string;
};

type AbaUltimasCompras = 'notas' | 'ordens' | 'produtos';
type ColunaOrdenacao = 'numero' | 'data' | 'total' | 'situacao' | 'descricao' | 'valorUnitario' | 'quantidade' | 'tipo';
type DirecaoOrdenacao = 'asc' | 'desc';
type ValorOrdenacao = string | number | null;

/** As linhas guardam o valor de cada coluna já pronto para exibir e ordenar. */
type NotaEntradaLinha = {
  idNFeEntrada: number;
  numero: string;
  data: string | null;
  total: number | null;
  situacao: string;
  classeBadge: string;
  iconeSituacao: string;
};

type OrdemCompraLinha = {
  idCompra: number;
  numero: string;
  data: string;
  total: number | null;
  situacao: string;
  corEtapa: string;
  iconeEtapa: string;
};

type ProdutoLinha = ItemCompradoFornecedorStruct & { tipo: string };

/**
 * Consulta somente leitura das últimas compras da pessoa (CO-01 a CO-09): uma aba por rotina, cada uma com
 * a permissão da sua rotina (PA-06, PA-07), carregada na primeira vez que é aberta e com a ordenação própria.
 */
@Component({
  selector: 'app-ultimas-compras-dialog',
  standalone: true,
  imports: [DatePipe, NgTemplateOutlet, FormsModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatSortModule, MatTableModule, MatTabsModule, MatTooltipModule, AbntPipe, EmptyStateComponent,
    LoadingBoxDefaultListComponent, TabsScrollDirective],
  templateUrl: './ultimas-compras-dialog.component.html',
  styleUrls: ['./ultimas-compras-dialog.component.css'],
})
export class UltimasComprasDialogComponent implements OnInit {
  public readonly abas: AbaUltimasCompras[] = ['notas', 'ordens', 'produtos'];
  public readonly colunasNotas = ['numero', 'data', 'total', 'situacao'];
  public readonly colunasOrdens = ['numero', 'data', 'total', 'situacao'];
  public readonly colunasProdutos = ['descricao', 'valorUnitario', 'quantidade', 'data', 'tipo', 'numero'];

  public possuiPermissaoNotas = false;
  public possuiPermissaoCompras = false;
  public notas: NotaEntradaLinha[] = [];
  public ordens: OrdemCompraLinha[] = [];
  public produtos: ProdutoLinha[] = [];
  /** As linhas na ordem da aba, recalculadas só quando os dados carregam ou a ordenação muda. */
  public notasExibidas: NotaEntradaLinha[] = [];
  public ordensExibidas: OrdemCompraLinha[] = [];
  public produtosExibidos: ProdutoLinha[] = [];
  public isCarregando: Record<AbaUltimasCompras, boolean> = { notas: true, ordens: true, produtos: true };
  /** CO-04: padrão Data decrescente, guardada por aba enquanto o dialog está aberto. */
  public ordenacao: Record<AbaUltimasCompras, { coluna: ColunaOrdenacao; direcao: DirecaoOrdenacao }> = {
    notas: { coluna: 'data', direcao: 'desc' },
    ordens: { coluna: 'data', direcao: 'desc' },
    produtos: { coluna: 'data', direcao: 'desc' },
  };
  public pesquisaProdutos = new FormControl('', { nonNullable: true });
  /** A pesquisa da última consulta, que decide o estado vazio (CO-07), e não o que está digitado. */
  public pesquisaAplicada = '';

  private readonly abasAbertas = new Set<AbaUltimasCompras>();

  constructor(
    private dialogRef: MatDialogRef<UltimasComprasDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UltimasComprasDialogData,
    private router: Router,
    private authService: AuthService,
    private notaFiscalEntradaListaService: NotaFiscalEntradaListaService,
    private compraService: CompraService,
    private orquestradorCompraService: OrquestradorCompraService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.possuiPermissaoNotas = permissoes.includes(PermissaoEnum.Suprimentos_NotasFiscaisEntrada_Visualizar);
    this.possuiPermissaoCompras = permissoes.includes(PermissaoEnum.Suprimentos_ProcessoCompras_Visualizar);
    this.abrirAba(0);
  }

  /** Cada aba consulta a API na primeira vez que é aberta; sem permissão, não consulta (PA-06). */
  public abrirAba(indice: number): void {
    const aba = this.abas[indice];
    if (!aba || this.abasAbertas.has(aba)) return;
    this.abasAbertas.add(aba);

    switch (aba) {
      case 'notas':
        if (this.possuiPermissaoNotas) this.carregarNotas();
        return;
      case 'ordens':
        if (this.possuiPermissaoCompras) this.carregarOrdens();
        return;
      case 'produtos':
        if (this.possuiPermissaoCompras) this.carregarProdutos();
        return;
    }
  }

  /** CO-03: aplica com Enter ou na lupa, nunca enquanto digita. */
  public pesquisarProdutos(): void {
    if (this.isCarregando.produtos) return;
    this.pesquisaAplicada = this.pesquisaProdutos.value.trim();
    this.carregarProdutos();
  }

  /** Cada aba guarda a sua ordenação; sem direção (terceiro clique), volta ao padrão Data decrescente. */
  public atualizarOrdenacao(aba: AbaUltimasCompras, ordenacao: Sort): void {
    this.ordenacao[aba] = ordenacao.direction
      ? { coluna: ordenacao.active as ColunaOrdenacao, direcao: ordenacao.direction }
      : { coluna: 'data', direcao: 'desc' };
    this.aplicarOrdenacao(aba);
  }

  public abrirNotaEntrada(idNFeEntrada: number): void {
    this.dialogRef.close();
    this.router.navigate(['/suprimentos/nota-fiscal-entrada/documento', idNFeEntrada], {
      queryParams: { modo: 'visualizar' },
    });
  }

  /** O Kanban de Compras abre o card da compra pelo parâmetro idCompra. */
  public abrirOrdemCompra(idCompra: number): void {
    this.dialogRef.close();
    this.router.navigate(['/suprimentos/processo-compras'], { queryParams: { idCompra } });
  }

  public abrirDocumentoDoProduto(produto: ProdutoLinha): void {
    if (produto.tipoDocumentoItemCompra === TipoDocumentoItemCompraEnum.NotaDeEntrada) {
      this.abrirNotaEntrada(produto.idDocumento);
      return;
    }
    this.abrirOrdemCompra(produto.idDocumento);
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  private carregarNotas(): void {
    this.isCarregando.notas = true;
    this.notaFiscalEntradaListaService.getUltimasDoEmitente(this.data.idPessoa).subscribe({
      next: response => {
        this.isCarregando.notas = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.notas = (response.notas ?? []).map(nota => ({
          idNFeEntrada: nota.idNFeEntrada,
          numero: `${nota.numero || '—'}${nota.serie ? ` / ${nota.serie}` : ''}`,
          data: nota.dataEmissao,
          total: nota.valorTotal,
          ...this.descreverSituacaoNota(nota),
        }));
        this.aplicarOrdenacao('notas');
      },
      error: () => {
        this.isCarregando.notas = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar as notas de entrada agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private carregarOrdens(): void {
    this.isCarregando.ordens = true;
    this.compraService.getUltimasDoFornecedor(this.data.idPessoa).subscribe({
      next: response => {
        this.isCarregando.ordens = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.ordens = (response.compras ?? []).map(compra => ({
          idCompra: compra.idCompra,
          numero: compra.codigo,
          data: compra.datetimeInclusao,
          total: compra.valorTotal,
          situacao: compra.nomeEtapa,
          // Mesma cor padrão do Kanban de Compras para etapa sem cor.
          // Mesmas cores e ícone reservas do Kanban de Compras quando a etapa não tem os seus.
          corEtapa: compra.corEtapa || '#1E5DB8',
          iconeEtapa: compra.iconeEtapa || 'description',
        }));
        this.aplicarOrdenacao('ordens');
      },
      error: () => {
        this.isCarregando.ordens = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar as ordens de compra agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private carregarProdutos(): void {
    this.isCarregando.produtos = true;
    this.orquestradorCompraService.getItensDoFornecedor(this.data.idPessoa, this.pesquisaAplicada).subscribe({
      next: response => {
        this.isCarregando.produtos = false;
        if (response.isError) {
          this.produtos = [];
          this.aplicarOrdenacao('produtos');
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.produtos = (response.itens ?? []).map(item => ({ ...item, tipo: this.descreverTipo(item.tipoDocumentoItemCompra) }));
        this.aplicarOrdenacao('produtos');
      },
      error: () => {
        this.isCarregando.produtos = false;
        this.produtos = [];
        this.aplicarOrdenacao('produtos');
        this.alertService.show('Aviso',
          'Não foi possível carregar os produtos agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  /** Cancelada prevalece sobre o status; senão vale o status da nota, como na lista de notas de entrada. */
  private descreverSituacaoNota(
    nota: NotaFiscalEntradaDoEmitenteStruct,
  ): Pick<NotaEntradaLinha, 'situacao' | 'classeBadge' | 'iconeSituacao'> {
    if (nota.isCancelada) return { situacao: 'NF-e cancelada', classeBadge: 'badge-vermelho', iconeSituacao: 'cancel' };
    return {
      situacao: rotuloStatusNFeEntrada(nota.statusNFeEntrada),
      classeBadge: classeBadgeStatusNFeEntrada(nota.statusNFeEntrada),
      iconeSituacao: iconeStatusNFeEntrada(nota.statusNFeEntrada),
    };
  }

  private descreverTipo(tipoDocumentoItemCompra: number): string {
    switch (tipoDocumentoItemCompra) {
      case TipoDocumentoItemCompraEnum.OrdemDeCompra: return 'Ordem de compra';
      case TipoDocumentoItemCompraEnum.NotaDeEntrada: return 'Nota de entrada';
      default: return '-';
    }
  }

  private aplicarOrdenacao(aba: AbaUltimasCompras): void {
    switch (aba) {
      case 'notas':
        this.notasExibidas = this.ordenar(this.notas, aba);
        return;
      case 'ordens':
        this.ordensExibidas = this.ordenar(this.ordens, aba);
        return;
      case 'produtos':
        this.produtosExibidos = this.ordenar(this.produtos, aba);
        return;
    }
  }

  private ordenar<T extends Partial<Record<ColunaOrdenacao, ValorOrdenacao>>>(linhas: T[], aba: AbaUltimasCompras): T[] {
    const { coluna, direcao } = this.ordenacao[aba];
    // Os números dentro do texto ("45 / 1") ganham zeros à esquerda para ordenar pelo valor, e não dígito a dígito.
    const chave = (valor: string | number) =>
      typeof valor === 'string' ? valor.replace(/\d+/g, digitos => digitos.padStart(20, '0')) : valor;

    return [...linhas].sort((primeira, segunda) => {
      const primeiroValor = primeira[coluna] ?? null;
      const segundoValor = segunda[coluna] ?? null;

      // Sem valor fica sempre no fim, nas duas direções.
      if (primeiroValor === null || segundoValor === null) {
        return primeiroValor === segundoValor ? 0 : primeiroValor === null ? 1 : -1;
      }

      const resultado = compararValoresOrdenacao(chave(primeiroValor), chave(segundoValor));
      return direcao === 'asc' ? resultado : -resultado;
    });
  }
}
